import { Fragment, useRef, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useTranslation } from '@klv/hooks/useTranslation';
import { withOptimizely } from '@optimizely/react-sdk';

import * as Yup from 'yup';
import { getStoredTrackingParams } from '@klv/shared/utils/attribution';
import { getUrlTrackingParams, handleFieldBlur, windowIsDefined } from '@klv/shared/utils/functions';
import { getCookieValue } from '@klv/shared/utils/cookies';
import { debouncedBeginEvent, combineGclidAndGaIdValue } from '@klv/shared/utils/google-tag-manager';

import Button from '@klv/atoms/button';
import CheckboxInput from '@klv/molecules/form-field/checkbox';
import HiddenInput from '@klv/molecules/form-field/hidden';
import SelectInput from '@klv/molecules/form-field/select';
import TextInput from '@klv/molecules/form-field/text';
import { executeReCaptcha } from '@klv/shared/utils/recaptcha';
import AutofillSync from '../../shared/autofill-sync';
import * as styles from '../../styles/forms.module.scss';
import { generateHBInitialValues, ScrollToError } from '../../utils';
import { submitToHubSpot } from '../../utils/hubSpot-submit-form';
// this is an empty file that gets real data during the build
import hubSpotDemoFormData from './static/hubspot-demo-form-data.json';
// this is the local development fallback so the form can work locally
import localHubSpotDemoFormData from './static/local-hubspot-demo-form-data.json';

import type { FocusEvent, FormEvent } from 'react';
import type { FormikProps } from 'formik';
import type { WithOptimizelyProps } from '@optimizely/react-sdk';
import type { IInitialFormValues, IHubSpotData, IStaticHubSpotDemoForm, IHubSpotDataOrEmpty } from '../../types';

const HubSpotDemoForm = ({
  setSuccess, settings, backgroundColor = 'dark', optimizely,
}: IStaticHubSpotDemoForm & WithOptimizelyProps) => {
  const { t } = useTranslation();
  const formData: IHubSpotDataOrEmpty = process.env.NODE_ENV === 'development' ? localHubSpotDemoFormData as IHubSpotData : hubSpotDemoFormData as IHubSpotData;
  const urlTrackingParams = getUrlTrackingParams();
  const storedTrackingParams = getStoredTrackingParams();
  const errorElement = useRef<HTMLDivElement>(null);
  const formikRef = useRef<FormikProps<IInitialFormValues> | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [formState, setFormState] = useState({});

  // This is to generate the initial validation schema.
  // This is to allow for flexibility in the schema and for marketing to add fields
  const getHBValidationSchema = (data: IHubSpotDataOrEmpty) => {
    const validationSchemaBase: Record<string, any> = {};
    if ('formFieldGroups' in data) {
      data.formFieldGroups.forEach((fieldGroup) => {
        fieldGroup.fields.forEach((field) => {
          if (field.required) {
            if (field.name === 'phone') {
              validationSchemaBase[field.name] = Yup.string()
                .matches(/^[\d|+|(]+[)|\d|\s|-]*[\d]$/, t('organisms_forms_fields_phone_errors_matches', { defaultValue: 'Please make sure your phone number includes your area code or country code' }))
                .max(15, t('organisms_forms_fields_phone_errors_max', { defaultValue: 'Number must have 15 or fewer characters' }))
                .required(t(`organisms_forms_fields_${field.name}_errors_required`, { defaultValue: 'This field is required' }));
            } else if (field.name === 'email') {
              validationSchemaBase[field.name] = Yup.string()
                .email(t('organisms_forms_fields_email_errors_email', { defaultValue: 'Please make sure your email address includes a single @, followed by a valid domain (name@example.com)' }))
                .required(t(`organisms_forms_fields_${field.name}_errors_required`));
            } else if ((field.name === 'firstname' || field.name === 'lastname')) {
              validationSchemaBase[field.name] = Yup.string().required(t(`organisms_forms_fields_${field.name}_errors_required`));
            } else if (field.name === 'website') {
              validationSchemaBase[field.name] = Yup.string()
                .url(t('organisms_forms_fields_company_website_errors_url') || 'Please make sure your company website includes a valid domain (https://example.com)')
                .required(t('organisms_forms_fields_company_website_errors_required') || 'Required');
            } else {
              validationSchemaBase[field.name] = Yup.string().required(t('organisms_forms_fields_general_errors_required'));
            }
          } else {
            validationSchemaBase[field.name] = Yup.string();
          }
        });
      });

      data.metaData.forEach((meta) => {
        if (meta.name === 'legalConsentOptions') {
          validationSchemaBase.consent_checkbox = Yup.string();
          validationSchemaBase.consentCheckboxSubscriptionId = Yup.string();
          validationSchemaBase.consentCheckboxLanguage = Yup.string();
          validationSchemaBase.implicitConsentLanguage = Yup.string();
        }
      });
    }

    // added afterwards because this will never come from HubSpot
    validationSchemaBase['g-recaptcha-response'] = Yup.string();

    return Yup.object().shape(validationSchemaBase);
  };

  const errorText = {
    generic: t('organisms_forms_errors_generic') || 'Your request was not successful. Please try again.',
  };

  // Create a reset function
  const resetRecaptcha = () => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('g-recaptcha-response', '');
    }
  };

  useEffect(() => {
    debouncedBeginEvent('demo');
    optimizely?.track('demo begin');
  }, [optimizely]);



  if (!formData || Object.keys(formData).length === 0) {
    return null;
  }
  const initialValues = formData ? generateHBInitialValues(formData, urlTrackingParams, storedTrackingParams, formState) : {};
  const validationSchemaBase = formData ? getHBValidationSchema(formData) : Yup.object().shape({});
  return (
    <div>
      {errors?.length > 0 && (
        <div className={styles.errorContainer}>
          {errors.map((error: string) => (
            <ScrollToError key={error} innerRef={errorElement}>
              <div className={styles.formError}>{error}</div>
            </ScrollToError>
          ))}
        </div>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemaBase}
        onSubmit={async (values, formikBag) => {
          // Get CHEQ request ID at submission time
          let cheqReqId = '';
          if (windowIsDefined() && typeof window._cq !== 'undefined') {
            window?._cq?.injectRequestId();
            const requestId = window?._cq.getRequestId();
            if (requestId) {
              cheqReqId = requestId;
            }
          }

          try {
            // Get a fresh reCAPTCHA token on form submission
            if (!executeReCaptcha) {
              setErrors([t('organisms_forms_errors_recaptcha') || 'reCaptcha is not available']);
              return false;
            }

            const token = await executeReCaptcha('demo_form_submit');
            if (!token) {
              setErrors([t('organisms_forms_errors_recaptcha') || 'reCaptcha verification failed']);
              return false;
            }
            values['g-recaptcha-response'] = token;

            const googleClientId = await combineGclidAndGaIdValue(urlTrackingParams?.gclid);
            formikBag.setTouched({ 'product_interest': true });
            const irclickid = urlTrackingParams?.irclickid || storedTrackingParams?.irclickid || '';
            values.gclid = `${googleClientId}${irclickid ? `:${irclickid}` : ''}`;
            values.irclickid = irclickid;
            values.implicitConsentLanguage = values.implicitConsentLanguage;
            values.consentCheckboxLanguage = values.consentCheckboxLanguage;
            values.consentCheckboxSubscriptionId = values.consentCheckboxSubscriptionId;
            values.cq_req_id = String(cheqReqId);
            
            const context = {
              'hutk': getCookieValue('hubspotutk') ?? undefined,
              'pageUri': windowIsDefined() ? window.location.href : '',
              'pageName': document.title,
            };

            const { setFieldError } = formikBag;
            await submitToHubSpot({ formData: values, context, settings, setErrors, setSuccess, resetRecaptcha, errorText, setFieldError });

            optimizely?.track('demo complete');
            
          } catch (error) {
            console.error('Form submission failed:', error);
            setErrors([t('organisms_forms_errors_generic') || 'Your request was not successful. Please try again.']);
            resetRecaptcha();
          }
        }}
      >
        {(formik) => {
          formikRef.current = formik;
          return (
            <Form id="form--demoRequest-hubSpot">
              {formData && formData.formFieldGroups.map((fieldGroup, index) => (
                <Fragment key={`fieldGroup-${JSON.stringify(fieldGroup)}`}>
                  {fieldGroup.fields.map((field) => {
                    if (!field.hidden) {
                      if (field.fieldType === 'text' || field.fieldType === 'email' || field.fieldType === 'phonenumber' || field.fieldType === 'url') {
                        const findFieldType = field.fieldType === 'email' ? 'email' : field.fieldType === 'phonenumber' ? 'tel' : 'text';
                        return (
                          <TextInput
                            key={`input-${field.name}`}
                            backgroundColor={backgroundColor}
                            id={`demoForm-${field.name}`}
                            label={field.label}
                            name={field.name}
                            type={findFieldType}
                            placeholder={field.placeholder}
                            onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
                            autofocus={index === 0}
                          />
                        );
                      }
                      if (field.fieldType === 'select') {
                        return (
                          <SelectInput
                            key={`select-${field.name}`}
                            backgroundColor={backgroundColor}
                            id={`demoForm-${field.name}`}
                            label={field.label}
                            name={field.name}
                            required={field.required}
                            onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
                          >
                            {field.unselectedLabel &&  <option value="" disabled hidden dangerouslySetInnerHTML={{ __html: field.unselectedLabel }} />}
                            {field && field.options && field.options.map((option) => (
                              <option key={`option-${field.name}-${option.value}`} value={option.value} dangerouslySetInnerHTML={{ __html: option.label }} />
                            ))}
                          </SelectInput>
                        );
                      }
                      if (field.fieldType === 'booleancheckbox') {
                        return (
                          <div key={`checkbox-wrapper-${field.name}`} className={styles.marginBottom25}>
                            <CheckboxInput
                              key={`checkbox-${field.name}`}
                              backgroundColor={backgroundColor}
                              id={`demoForm-${field.name}`}
                              label={field.label || 'Tell me more. Sign me up for marketing communications from Klaviyo.*'}
                              size="small"
                              name={field.name}
                              onChange={(event: FormEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
                            />
                          </div>
                        );
                      } else return null;
                    } else if (field.fieldType === 'hidden') {
                      return <HiddenInput key={`hidden-${field.name}`} id={`demoForm-${field.name}`} name={field.name} />;
                    }
                  })}
                </Fragment>
              ))}
              <Button
                text={formik.isSubmitting ? t('organisms_forms_demo_buttonSubmitting') || 'Submitting...' : t('organisms_forms_demo_button') || 'Get a demo'}
                buttonColor={backgroundColor === 'dark' ? 'light' : 'dark'}
                variant="primary"
                type="submit"
              />
              {formData && formData.metaData.map((meta) => {
                if (meta.name === 'legalConsentOptions') {
                  const parsedMeta = JSON.parse(meta.value);
                  const communicationConsentCheckbox = parsedMeta.communicationConsentCheckboxes[0];
                  const consentLanguage = communicationConsentCheckbox.label || '';

                  return (
                    <div key={`checkbox-wrapper-${meta.name}`} className={styles.margin25}>
                      <CheckboxInput
                        key={`checkbox-${meta.name}`}
                        backgroundColor={backgroundColor}
                        id={`demoForm-${meta.name}`}
                        label={consentLanguage}
                        size="small"
                        name="consent_checkbox"
                        dangerouslySetHTML={true}
                        onChange={(event: FormEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
                      />
                    </div>
                  );
                } else return null;
              })}
              <AutofillSync formik={formik} fields={['email']} />

              <div className="p3 smPaddingTop" dangerouslySetInnerHTML={{ __html: t('organisms_forms_demo_recaptcha_privacy') }} />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default withOptimizely(HubSpotDemoForm);
